/**
 * vb365-repo-migration.js — VB365 Repository Migration Section
 *
 * Owns:
 *   - buildDefaultVb365RepoMigrationEntry  (data model factory)
 *   - DOM refs for the VB365 repository migration section
 *   - syncVb365RepoMigrationSectionVisibility (exported) — show/hide based on
 *     presence of a named VB365 server AND vb365RepositoryServers > 0 in step 1
 *   - renderVb365RepoMigrationSection — renders all scope rows
 *   - All delegated DOM events for the rows host and add-migration button
 *
 * Each scope row captures:
 *   - targetRepoType — storage type being migrated to (source type is already
 *                      defined in Step 1 when the VB365 repository is added)
 *
 * Repository count and data volume (TB) are both derived from Step 1 at
 * calculation time — not stored on the scope object:
 *   count  → vb365RepositoryServers in existingEnvironment
 *   data   → existingEnvironmentVb365RepoConsumedTbs per-slot values
 *
 * VB365 version (v6/v7/v8) is read at effort-calculation time from
 * site.existingEnvironmentServerVersions.vb365 in recalc.js — not stored per scope.
 *
 * Exports:
 *   syncVb365RepoMigrationSectionVisibility — called from syncDesignOptionFields in script.js
 *
 * Window bridge written:
 *   window.vb365RepoMigrationActive — boolean; consumed by syncMigrationGroupVisibility in helpers.js
 */

import { sites, vb365RepoMigrationScopes } from './state.js';
import { vb365RepoTypeOptions } from './workload-defs.js';
import { escapeHtml } from './utils.js';
import { scheduleRenderEstimate, getExistingEnvironmentSnapshotQuantity } from './recalc.js';
import {
    syncMigrationGroupVisibility,
    createVb365RepoMigrationId,
} from './helpers.js';

// ── VB365 repository migration data model ─────────────────────────────────────

export function buildDefaultVb365RepoMigrationEntry() {
    return {
        id:            createVb365RepoMigrationId(),
        targetRepoType: 'object-s3',  // storage type being migrated to
    };
}

// ── Source type helper ────────────────────────────────────────────────────────

/**
 * Returns the VB365 repository storage type recorded in Step 1 for the first
 * site that has at least one vb365RepositoryServer in its existing environment.
 * Reads from the per-slot dict (slot 0) as the representative source type.
 * Falls back to 'block' when no site has set it yet.
 */
function getSourceRepoType() {
    for (const site of sites) {
        if (getExistingEnvironmentSnapshotQuantity([site], 'vb365RepositoryServers') > 0) {
            const repoTypes = site.existingEnvironmentVb365RepoTypes || {};
            return repoTypes[0] || 'block';
        }
    }
    return 'block';
}

/** Returns the display label for a repo type value, or the value itself if unknown. */
function repoTypeLabel(value) {
    return vb365RepoTypeOptions.find((o) => o.value === value)?.label ?? value;
}

// ── DOM refs ──────────────────────────────────────────────────────────────────

const vb365RepoMigrationRowsHost = document.getElementById('vb365-repo-migration-rows-host');

// Window bridge — read by syncMigrationGroupVisibility in helpers.js
window.vb365RepoMigrationActive = false;

// ── Visibility helpers ────────────────────────────────────────────────────────

/** Returns true when at least one site has a named VB365 server instance. */
function hasVb365Server() {
    return sites.some((site) =>
        Array.isArray(site.veeamServers) && site.veeamServers.some((s) => s.type === 'vb365')
    );
}

/** Returns true when at least one site has one or more VB365 repository servers
 *  recorded in the existing environment component counts. */
function hasVb365Repositories() {
    return sites.some((site) =>
        getExistingEnvironmentSnapshotQuantity([site], 'vb365RepositoryServers') > 0
    );
}

/** Returns the total number of VB365 repository servers across all sites (from Step 1). */
function totalVb365RepoCount() {
    return sites.reduce((sum, site) =>
        sum + getExistingEnvironmentSnapshotQuantity([site], 'vb365RepositoryServers'), 0
    );
}

/**
 * Shows/hides the Add VB365 Repository Migration button.
 * Caps the number of scope rows at the number of VB365 repos configured in Step 1,
 * since each row represents one repository being migrated.
 */
function syncVb365AddButtonAvailability() {
    const addBtn = document.getElementById('add-vb365-repo-migration-btn');
    if (!addBtn) return;
    const shouldShow = hasVb365Server() && hasVb365Repositories();
    if (!shouldShow) { addBtn.hidden = true; return; }
    addBtn.hidden = vb365RepoMigrationScopes.length >= totalVb365RepoCount();
}

// ── Exported visibility sync ──────────────────────────────────────────────────

/** Show/hide the VB365 add button and update the active flag.
 *  Requires both a named VB365 server AND VB365 repository servers in step 1. */
export function syncVb365RepoMigrationSectionVisibility() {
    const shouldShow = hasVb365Server() && hasVb365Repositories();
    window.vb365RepoMigrationActive = shouldShow;
    renderVb365RepoMigrationSection(); // also calls syncVb365AddButtonAvailability
    syncMigrationGroupVisibility();
}

// ── Section render ────────────────────────────────────────────────────────────

/** Renders all VB365 repository migration scope rows into the host element. */
/** Syncs the shared empty-state message and table-wrap visibility.
 *  Called after rendering — checks both tbodies so each module stays decoupled. */
function syncRepoMigrationEmptyState() {
    const hasVb365Rows = vb365RepoMigrationRowsHost && vb365RepoMigrationRowsHost.querySelector('tr[data-vb365rm-scope]') !== null;
    const hasVbrRows   = document.querySelector('#vbr-repo-migration-rows-host tr[data-vbrrm-scope]') !== null;
    const hasAnyRows   = hasVbrRows || hasVb365Rows;
    const emptyEl   = document.getElementById('repo-migration-empty-state');
    const tableWrap = document.getElementById('repo-migration-table-wrap');
    if (emptyEl)   emptyEl.hidden   = hasAnyRows;
    if (tableWrap) tableWrap.hidden = !hasAnyRows;
}

/** Renders all VB365 repository migration scope rows into the shared table tbody. */
function renderVb365RepoMigrationSection() {
    if (!vb365RepoMigrationRowsHost) return;

    if (vb365RepoMigrationScopes.length === 0) {
        vb365RepoMigrationRowsHost.innerHTML = '';
        syncRepoMigrationEmptyState();
        syncVb365AddButtonAvailability();
        return;
    }

    const svgClose = `<svg width="11" height="11" viewBox="0 0 12 12" fill="none" aria-hidden="true"><path d="M2 2l8 8M10 2l-8 8" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>`;

    // Source repo type is read from Step 1 (slot 0 of site.existingEnvironmentVb365RepoTypes)
    const sourceRepoType  = getSourceRepoType();
    const sourceLabelText = repoTypeLabel(sourceRepoType);

    const rowsHtml = vb365RepoMigrationScopes.map((scope) => {
        // ── Target repo type select ──────────────────────────────────────────
        const targetOpts = vb365RepoTypeOptions.map((opt) => {
            const sel = scope.targetRepoType === opt.value ? ' selected' : '';
            return `<option value="${escapeHtml(opt.value)}"${sel}>${escapeHtml(opt.label)}</option>`;
        }).join('');

        return `<tr class="hvm-row" data-vb365rm-scope="${scope.id}">
            <td class="hvm-td hvm-td--type"><span class="hvm-badge hvm-badge--vb365">VB365</span></td>
            <td class="hvm-td hvm-td--from">
                <label class="hvm-field-label">Source repository type <span class="hvm-th-hint">(from Step 1)</span></label>
                <span class="hvm-readonly-value" title="Set in Step 1 on the VB365 Repository Servers chip">${escapeHtml(sourceLabelText)}</span>
            </td>
            <td class="hvm-td hvm-td--to">
                <label class="hvm-field-label">Target repository type</label>
                <select class="hvm-select" data-vb365rm-target-select="${scope.id}" aria-label="Target repository type">
                    ${targetOpts}
                </select>
            </td>
            <td class="hvm-td hvm-td--opts"></td>
            <td class="hvm-td hvm-td--remove">
                <button type="button" class="hvm-remove-btn" data-vb365rm-remove="${scope.id}" aria-label="Remove migration scope">${svgClose}</button>
            </td>
        </tr>`;
    }).join('');

    vb365RepoMigrationRowsHost.innerHTML = rowsHtml;
    syncRepoMigrationEmptyState();
    syncVb365AddButtonAvailability();
}

// ── Section events ────────────────────────────────────────────────────────────

// Add migration scope
document.getElementById('add-vb365-repo-migration-btn')?.addEventListener('click', () => {
    if (vb365RepoMigrationScopes.length >= totalVb365RepoCount()) return; // guard: all repos claimed
    vb365RepoMigrationScopes.push(buildDefaultVb365RepoMigrationEntry());
    renderVb365RepoMigrationSection();
    scheduleRenderEstimate();
});

// Delegated click — remove button
document.getElementById('vb365-repo-migration-rows-host')?.addEventListener('click', (e) => {
    const removeBtn = e.target.closest('[data-vb365rm-remove]');
    if (removeBtn) {
        const scopeId = parseInt(removeBtn.dataset.vb365rmRemove, 10);
        const idx = vb365RepoMigrationScopes.findIndex((s) => s.id === scopeId);
        if (idx !== -1) {
            vb365RepoMigrationScopes.splice(idx, 1);
            renderVb365RepoMigrationSection();
            scheduleRenderEstimate();
        }
    }
});

// Delegated change — target type select
document.getElementById('vb365-repo-migration-rows-host')?.addEventListener('change', (e) => {
    const targetSelect = e.target.closest('[data-vb365rm-target-select]');
    if (targetSelect) {
        const scopeId = parseInt(targetSelect.dataset.vb365rmTargetSelect, 10);
        const scope = vb365RepoMigrationScopes.find((s) => s.id === scopeId);
        if (scope) {
            scope.targetRepoType = targetSelect.value;
            scheduleRenderEstimate();
        }
        return;
    }
});


// ── End VB365 Repository Migration Section ────────────────────────────────────
